import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Title } from '@angular/platform-browser';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { SelfServiceService } from '../../services/self-service.service';
import { ParkingNotFoundDialog } from '../../pages/drive-in-form/drive-in-form.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-quality-booking',
  standalone: true,
  templateUrl: './quality-booking.component.html',
  imports: [MatButtonModule, MatIconModule, TranslocoModule, ReactiveFormsModule, MatInputModule, MatFormFieldModule, MatProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./quality-booking.component.css']
})
export class QualityBookingComponent implements OnInit, OnDestroy {
  site: 'quality' | 'aiden' = 'quality';
  logoUrl = '../../../assets/img/quality-hotel-airport-arlanda.svg';
  showDriveInForm = false;
  isLoading = false;
  registrationEntered = false;
  enteredRegistration = '';
  isEmbedded = false;

  driveInForm = new FormGroup({
    registrationNumber: new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(9), Validators.pattern(/^[A-Z0-9]{4,9}$/)])
  });

  @ViewChild('registrationInput') registrationInput!: ElementRef;

  constructor(
    private router: Router,
    private titleService: Title,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private selfServiceService: SelfServiceService,
    public translate: TranslocoService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.isEmbedded = window.self !== window.top;
    const url = this.router.url;
    if (url.includes('aiden')) {
      this.site = 'aiden';
      this.logoUrl = '../../../assets/img/Aiden-hotel-logo.svg';
    } else {
      this.site = 'quality';
      this.logoUrl = '../../../assets/img/quality-hotel-airport-arlanda.svg';
    }

    this.titleService.setTitle(this.site === 'aiden' ? 'Aiden' : 'Quality');

    // Check for public query parameter
    const publicParam = this.route.snapshot.queryParams['public'];
    if (publicParam === 'true') {
      localStorage.setItem(`${this.site}-public`, 'true');
      this.showDriveInForm = true;
    } else {
      localStorage.removeItem(`${this.site}-public`);
      this.showDriveInForm = false;
    }

    // Prevent back button navigation
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener('popstate', this.onBackButtonEvent);

  }

  onBackButtonEvent = () => {
    // Push again to prevent actual back navigation
    window.history.pushState(null, document.title, window.location.href);
  }

  ngOnDestroy() {
    window.removeEventListener('popstate', this.onBackButtonEvent);
  }

  selectTransfer() {
    const orgId = this.site === 'aiden'
      ? 'D6B9D8C6-8FB5-41A5-AC2E-B3FC007A8251'
      : '39218780-069A-4C6B-9925-22365A075C15';
    this.navigateTo('/booking-service/' + orgId);
  }

  selectShortTermParking() {
    const targetRoute = this.site === 'aiden'
      ? '/parkifly/aiden-short-term'
      : '/parkifly/quality-xpo-arlanda';

    if (!this.showDriveInForm) {
      this.navigateTo(targetRoute);
    } else {
      if (this.registrationEntered) {
        this.navigateTo(targetRoute);
      } else {
        this.showRegistrationRequiredMessage();
      }
    }
  }

  selectFullTermParking() {
    const targetRoute = this.site === 'aiden'
      ? '/parkifly/aiden-parkandfly'
      : '/parkifly/quality-xpo-arlanda-parkandfly';

    if (!this.showDriveInForm) {
      this.navigateTo(targetRoute);
    } else {
      if (this.registrationEntered) {
        this.navigateTo(targetRoute);
      } else {
        this.showRegistrationRequiredMessage();
      }
    }
  }

  selectDriveUpBooking() {
    this.navigateTo('/drivein');
  }

  regToUpper(): void {
    const control = this.driveInForm.get('registrationNumber');
    if (control?.value) {
      control.setValue(control.value.toUpperCase());
    }
  }

  clearForm(): void {
    this.driveInForm.reset();
    this.registrationEntered = false;
    this.enteredRegistration = '';
  }

  saveRegistration(): void {
    if (this.driveInForm.valid) {
      this.isLoading = true;
      const licensePlate = this.driveInForm.controls.registrationNumber.value || '';

      this.selfServiceService.driveUpAfterRegistration(licensePlate).then((result: any) => {
        this.isLoading = false;
        const status = result.status;
        if (status == 3) {
          // Navigate directly to drive-in details form
          const queryParams: any = {
            l: licensePlate,
            parkingId: result.parkingId,
            gateName: result.gateName,
            parkingFrom: result.parkingFrom,
            parkingTo: result.parkingTo,
            price: result.price?.amountIncludeVat,
            orderId: result.orderId,
            orgId: result.organizationId
          };
          if (result.vehicleOwner) {
            queryParams.vehicleOwner = JSON.stringify(result.vehicleOwner);
          }
          this.navigateTo('/parkifly/drive-in-details-form', queryParams);
        } else {
          // Show booking options for other statuses
          this.enteredRegistration = licensePlate;
          this.registrationEntered = true;
        }
      }).catch(() => {
        this.isLoading = false;
        // On error, still show booking options
        this.enteredRegistration = licensePlate;
        this.registrationEntered = true;
      });
    }
  }

  showRegistrationRequiredMessage() {
    const message = this.translate.translate('QUALITY_LANDING_PAGE.ENTER_REGISTRATION_NUMBER');
    this.snackBar.open(message, 'OK', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });

    // Scroll to registration input
    if (this.registrationInput) {
      this.registrationInput.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // Focus the input field
      setTimeout(() => {
        this.registrationInput.nativeElement.focus();
      }, 500);
    }
  }

  submitDriveInForm(): void {
    if (this.driveInForm.valid) {
      this.isLoading = true;
      const licensePlate = this.driveInForm.controls.registrationNumber.value;

      this.selfServiceService.driveUpAfterRegistration(licensePlate).then((result: any) => {
        this.isLoading = false;
        const status = result.status;
        if (status == 3) {
          const queryParams: any = {
            l: licensePlate,
            parkingId: result.parkingId,
            gateName: result.gateName,
            parkingFrom: result.parkingFrom,
            parkingTo: result.parkingTo,
            price: result.price?.amountIncludeVat,
            orderId: result.orderId,
            orgId: result.organizationId
          };
          if (result.vehicleOwner) {
            queryParams.vehicleOwner = JSON.stringify(result.vehicleOwner);
          }
          this.navigateTo('/parkifly/drive-in-details-form', queryParams);
        }
      }).catch(() => {
        this.isLoading = false;
        this.openParkingNotFoundDialog(licensePlate);
      });
    }
  }

  openParkingNotFoundDialog(licensePlate: string): void {
    this.dialog.open(ParkingNotFoundDialog, {
      width: '400px',
      data: { licensePlate }
    });
  }

  private navigateTo(route: string, queryParams?: any): void {
    if (this.isEmbedded) {
      let url = window.location.origin + route;
      if (queryParams) {
        const params = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
          if (queryParams[key] !== undefined && queryParams[key] !== null) {
            params.append(key, queryParams[key]);
          }
        });
        const paramStr = params.toString();
        if (paramStr) {
          url += '?' + paramStr;
        }
      }
      window.open(url, '_blank');
    } else {
      if (queryParams) {
        this.router.navigate([route], { queryParams });
      } else {
        this.router.navigate([route]);
      }
    }
  }

}